import styled from "@emotion/styled";
import { filter } from "lodash-es";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { TrackedEvent, trackEvent } from "@/domains/metrics";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonLoadingIndicator,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSModalDialog,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import { useAgentOverlayAnalytics } from "@/pages/agent/analytics/use-agent-overlay-analytics";
import { AgentFilterSearchField } from "@/pages/agent/components/agent-filter-search-field";
import { AgentFilterSelect } from "@/pages/agent/components/agent-filter-select";
import { AgentPageFilterBar, AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { buildRoutineSortFilterItems } from "@/pages/agent/routines/agent-routine-filter-items";
import {
  buildRoutineRows,
  getSortedRoutineRows,
} from "@/pages/agent/routines/agent-routines-page.utils";
import { AgentRoutinesSection } from "@/pages/agent/routines/agent-routines-section";
import {
  ALL_ROUTINE_STATUSES_FILTER,
  DEFAULT_ROUTINE_SORT,
  useAgentRoutinesSearchParams,
} from "@/pages/agent/routines/use-agent-routines-search-params";
import {
  buildAgentStatusFilterItems,
  normalizeStatusValue,
} from "@/pages/agent/utils/agent-status.utils";
import {
  acceptRoutineShareLink,
  previewRoutineShareLink,
} from "@/pages/routine-share/routine-share-api";
import type { RoutineSharePreview } from "@/pages/routine-share/routine-share-api";
import { useAppStore } from "@/store";
import { AGENT_ROUTINE_IMPORT_SEARCH_PARAM } from "@/store/agent-overlay/types";
import { AssistantRoutineStatus } from "@/store/assistant/types";

const ADAPTATION_HANDOFF_DELAY_MS = 2000;

type RoutineImportState =
  | { kind: "loading" }
  | { kind: "ready"; preview: RoutineSharePreview }
  | { kind: "adapting"; preview: RoutineSharePreview }
  | { kind: "unavailable" };

export const AgentRoutinesPage = observer(() => {
  const { store } = useAppStore();
  const analytics = useAgentOverlayAnalytics();
  const { statusFilter, searchQuery, sortFilter, updateRoutineFilters } =
    useAgentRoutinesSearchParams();
  const routines = store.assistantRoutines.currentAssistantModels;
  const routineRows = buildRoutineRows({ routines });
  const dataLoaded = store.assistantRoutines.currentAssistantModelsLoaded;
  const isRoutineSharingEnabled = store.agentOverlay.isSkillsFeatureEnabled;

  const [searchParams, setSearchParams] = useSearchParams();
  const importToken = searchParams.get(AGENT_ROUTINE_IMPORT_SEARCH_PARAM);
  const [importState, setImportState] = useState<RoutineImportState>({ kind: "loading" });
  const [isAccepting, setIsAccepting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const clearImportParam = () => {
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete(AGENT_ROUTINE_IMPORT_SEARCH_PARAM);
        return next;
      },
      { replace: true }
    );
    setImportError(null);
  };

  useEffect(() => {
    if (!importToken) return;

    let isActive = true;
    setImportState({ kind: "loading" });
    setImportError(null);
    setIsAccepting(false);

    if (!isRoutineSharingEnabled) {
      trackEvent(TrackedEvent.MemAgentShareLinkUnavailable, { item_kind: "routine" });
      setImportState({ kind: "unavailable" });
      return;
    }

    const loadPreview = async () => {
      try {
        const preview = await previewRoutineShareLink({ verificationToken: importToken });
        if (!isActive) return;
        trackEvent(TrackedEvent.MemAgentShareLinkView, {
          item_kind: "routine",
          already_saved: false,
        });
        setImportState({ kind: "ready", preview });
      } catch {
        if (!isActive) return;
        trackEvent(TrackedEvent.MemAgentShareLinkUnavailable, { item_kind: "routine" });
        setImportState({ kind: "unavailable" });
      }
    };

    void loadPreview();

    return () => {
      isActive = false;
    };
  }, [importToken, isRoutineSharingEnabled]);

  const acceptImportedRoutine = async () => {
    if (!importToken || importState.kind !== "ready") return;

    setIsAccepting(true);
    setImportError(null);
    try {
      await acceptRoutineShareLink({ verificationToken: importToken });
      trackEvent(TrackedEvent.MemAgentShareAccept, { item_kind: "routine" });
      setImportState({ kind: "adapting", preview: importState.preview });
      window.setTimeout(clearImportParam, ADAPTATION_HANDOFF_DELAY_MS);
    } catch {
      trackEvent(TrackedEvent.MemAgentShareAcceptFailed, { item_kind: "routine" });
      setImportError("Mem could not add this routine. Please try again.");
    } finally {
      setIsAccepting(false);
    }
  };

  const importOwnerLabel =
    importState.kind === "ready" || importState.kind === "adapting"
      ? importState.preview.owner.display_name || "Another Mem user"
      : null;
  const importPreview =
    importState.kind === "ready" || importState.kind === "adapting" ? importState.preview : null;
  const isImportAdapting = importState.kind === "adapting";

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredRoutineRows = filter(routineRows, routineRow => {
    const matchesStatus =
      statusFilter === ALL_ROUTINE_STATUSES_FILTER ||
      normalizeStatusValue({ status: routineRow.routine.status }) === statusFilter;
    const matchesSearch =
      !normalizedSearchQuery || routineRow.searchableText.includes(normalizedSearchQuery);

    return matchesStatus && matchesSearch;
  });
  const sortedRoutineRows = getSortedRoutineRows({
    routineRows: filteredRoutineRows,
    sortKey: sortFilter,
  });

  const statusFilterItems = buildAgentStatusFilterItems({
    allStatusesFilter: ALL_ROUTINE_STATUSES_FILTER,
    statusFilter,
    statusFilterValues: [
      AssistantRoutineStatus.Active,
      AssistantRoutineStatus.Paused,
      AssistantRoutineStatus.Unreviewed,
    ],
  });
  const sortFilterItems = buildRoutineSortFilterItems();
  const queryKey = JSON.stringify({
    searchQuery: normalizedSearchQuery,
    sortFilter,
    statusFilter,
  });
  const hasActiveFilters =
    normalizedSearchQuery.length > 0 || statusFilter !== ALL_ROUTINE_STATUSES_FILTER;

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Routines}>
      <AgentPageFilterBar>
        <AgentFilterSelect
          label="Sort"
          selectedKey={sortFilter}
          defaultKey={DEFAULT_ROUTINE_SORT}
          items={sortFilterItems}
          leadingIcon={MDSIconKind.SortAlt}
          alwaysActive
          onSelectionChange={({ key }) => {
            analytics.trackFilterChange({ filterKind: "sort", filterValue: key });
            updateRoutineFilters({ nextSortFilter: key });
          }}
          onClear={() => {
            analytics.trackFilterChange({
              filterKind: "sort",
              filterValue: DEFAULT_ROUTINE_SORT,
            });
            updateRoutineFilters({ nextSortFilter: DEFAULT_ROUTINE_SORT });
          }}
        />

        <AgentFilterSelect
          label="Status"
          selectedKey={statusFilter}
          defaultKey={ALL_ROUTINE_STATUSES_FILTER}
          items={statusFilterItems}
          onSelectionChange={({ key }) => {
            analytics.trackFilterChange({ filterKind: "status", filterValue: key });
            updateRoutineFilters({ nextStatusFilter: key });
          }}
          onClear={() => {
            analytics.trackFilterChange({
              filterKind: "status",
              filterValue: ALL_ROUTINE_STATUSES_FILTER,
            });
            updateRoutineFilters({ nextStatusFilter: ALL_ROUTINE_STATUSES_FILTER });
          }}
        />

        <AgentFilterSearchField
          ariaLabel="Filter routines by name or description"
          placeholder="Filter by name or description"
          value={searchQuery}
          onChange={value => {
            analytics.trackFilterChange({
              filterKind: "search",
              queryLength: value.trim().length,
            });
            updateRoutineFilters({ nextSearchQuery: value });
          }}
        />
      </AgentPageFilterBar>

      <AgentRoutinesSection
        dataLoaded={dataLoaded}
        hasActiveFilters={hasActiveFilters}
        isRoutineSharingEnabled={isRoutineSharingEnabled}
        queryKey={queryKey}
        routineRows={sortedRoutineRows}
        onOpenActivities={({ routineId }) => {
          store.agentOverlay.openActivities({ kind: "routine", routineId });
        }}
      />

      <StyledImportModalDialog
        isOpen={importToken !== null && importPreview !== null}
        handleClose={clearImportParam}
      >
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          Import shared routine
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          {isImportAdapting ? (
            <StyledCenteredImportStatus aria-live="polite">
              <MDSIcon color={MDSIconColor.Secondary} kind={MDSIconKind.Spinner} />
              <MDSText variant={MDSTextVariant.ParagraphS}>
                Mem Agent is going to adapt this routine for you
              </MDSText>
            </StyledCenteredImportStatus>
          ) : (
            <StyledImportContent>
              <StyledImportPreview>
                <StyledImportEyebrow>
                  {importOwnerLabel} shared a Mem Agent routine
                </StyledImportEyebrow>
                <StyledImportTitle>
                  {importPreview?.link.routine.label || "Untitled routine"}
                </StyledImportTitle>
                <StyledImportBody>
                  {importPreview?.link.routine.description || "No description yet."}
                </StyledImportBody>
              </StyledImportPreview>
              <StyledImportMessage>Mem Agent adapts imported routines to you</StyledImportMessage>
              {importError ? <StyledError role="alert">{importError}</StyledError> : null}
            </StyledImportContent>
          )}
        </MDSDialogTextContent>
        {!isImportAdapting ? (
          <StyledImportButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
            <MDSButton
              isDisabled={isAccepting}
              variant={MDSButtonVariant.Transparent}
              onPress={clearImportParam}
            >
              Not now
            </MDSButton>
            <MDSButton
              isLoading={isAccepting}
              loadingIndicator={MDSButtonLoadingIndicator.Icon}
              variant={MDSButtonVariant.Emphasis}
              onPress={() => void acceptImportedRoutine()}
            >
              Add routine
            </MDSButton>
          </StyledImportButtonGroup>
        ) : null}
      </StyledImportModalDialog>

      <MDSModalDialog
        isOpen={importToken !== null && importState.kind === "unavailable"}
        handleClose={clearImportParam}
      >
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          Shared routine unavailable
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            This shared routine is no longer available. The link may have been revoked or the
            routine deleted.
          </MDSText>
        </MDSDialogTextContent>
        <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
          <MDSButton variant={MDSButtonVariant.Contrast} onPress={clearImportParam}>
            Close
          </MDSButton>
        </MDSButtonGroup>
      </MDSModalDialog>
    </AgentPageLayout>
  );
});

const StyledImportModalDialog = styled(MDSModalDialog)`
  .mds-dialog {
    box-sizing: border-box;
    gap: ${({ theme }) => theme.spacing[3]};
    max-height: calc(100dvh - ${({ theme }) => theme.spacing[8]});
    max-width: calc(100vw - ${({ theme }) => theme.spacing[8]});
    padding: ${({ theme }) => theme.spacing[7]};
    width: min(560px, calc(100vw - ${({ theme }) => theme.spacing[8]}));
  }

  .mds-dialog [slot="header"] {
    min-width: 0;
    overflow-wrap: anywhere;
    padding-block: 0;
  }

  .mds-dialog button[aria-label="Close"] {
    margin: 0 0 0 auto;
  }

  [data-mds-name="dialog-body"] {
    margin-right: ${({ theme }) => `-${theme.spacing[2]}`};
    padding-right: ${({ theme }) => theme.spacing[2]};
  }

  @media (max-width: 560px) {
    .mds-dialog {
      max-height: calc(100dvh - ${({ theme }) => theme.spacing[4]});
      max-width: calc(100vw - ${({ theme }) => theme.spacing[4]});
      padding: ${({ theme }) => theme.spacing[5]};
      width: calc(100vw - ${({ theme }) => theme.spacing[4]});
    }
  }
`;

const StyledImportContent = styled.div`
  min-width: 0;
`;

const StyledImportButtonGroup = styled(MDSButtonGroup)`
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 420px) {
    width: 100%;

    > button {
      flex: 1 1 100%;
      justify-content: center;
    }
  }
`;

const StyledImportPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StyledImportEyebrow = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const StyledImportTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes["header-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  overflow-wrap: anywhere;
`;

const StyledImportBody = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const StyledImportMessage = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const StyledCenteredImportStatus = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: center;
  min-height: 160px;
  text-align: center;
`;

const StyledError = styled.div`
  color: ${({ theme }) => theme.colors.text.danger};
`;
